import React, { memo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Box, Grid } from '@mui/material';

import { AddButton } from 'components/_basic/Form/FieldArray/components/AddButton';
import { RemoveButton } from 'components/_basic/Form/FieldArray/components/RemoveButton';
import { TextInputControl } from 'components/_basic/Form/TextInput';

import { concatWithDot } from 'helpers/string';

import {
  COMPETITORS_COMPARISON_NAMES,
  FEATURES_FOR_COMPARISON_NAMES,
  featuresForComparisonValues,
} from '../utils/form';

import styles from './ComparisonFeatures.module.scss';

interface IProps {
  name: string;
  disabled: boolean;
}

const MAX_LENGTH = 5;

export const ComparisonFeatures: React.FC<IProps> = memo(({ name, disabled }) => {
  const { t } = useTranslation();
  const { control, getValues, setValue } = useFormContext();
  const featureName = concatWithDot(name, COMPETITORS_COMPARISON_NAMES.FEATURES_FOR_COMPARISON);
  const listName = concatWithDot(name, FEATURES_FOR_COMPARISON_NAMES.FEATURE);

  const { fields, append, remove } = useFieldArray({
    control,
    name: featureName,
  });

  const handleAddRow = () => append(featuresForComparisonValues);

  const handleRemoveFeature = (index: number) => {
    const currentFeatures = getValues(listName);
    if (currentFeatures) {
      const updatedFeatures = currentFeatures.filter((_: unknown, i: number) => i !== index);

      setValue(listName, updatedFeatures);
    }
  };

  const canAddMore = fields.length < MAX_LENGTH;
  const canRemove = fields.length > 1;

  return (
    <Grid container spacing={3} className={styles.container}>
      {fields.map((item, index) => (
        <Grid item xs={6} key={item.id} className={styles.rowContainer}>
          <Box className={styles.rowChild}>
            <TextInputControl
              control={control}
              name={concatWithDot(`${featureName}.${index}`, FEATURES_FOR_COMPARISON_NAMES.FEATURE)}
              id={concatWithDot(`${featureName}.${index}`, FEATURES_FOR_COMPARISON_NAMES.FEATURE)}
              label={t('marketAnalysisForm.competitors.feature')}
              disabled={disabled}
              maxLength={150}
            />
          </Box>
          <RemoveButton
            canRemove={canRemove}
            buttonStyles={styles.removeButton}
            disabled={disabled}
            onClick={() => {
              handleRemoveFeature(index);
              remove(index);
            }}
          />
        </Grid>
      ))}
      <Grid item xs={6} className={styles.addButtonWrapper}>
        <AddButton
          canAddMore={canAddMore}
          onClick={handleAddRow}
          className={styles.addButton}
          variant="text"
          disabled={disabled}
        >
          {t('marketAnalysisForm.competitors.feature')}
        </AddButton>
      </Grid>
    </Grid>
  );
});
